/**
 * Cities, service areas, localities and travel zones — `/v1/admin/geo*`.
 *
 * **A city carries two states that cannot contradict each other.** `launch_stage` is the
 * marketing/ops word the website reads; `is_launched` is the boolean the booking API reads. The
 * server keeps them consistent by constraint — a city can never be `live` while refusing bookings,
 * nor `seo_only` while accepting them — so the console changes the *stage* and reads the boolean
 * back rather than setting both and hoping.
 *
 * **Archiving is not deleting.** A city with a page and a waitlist keeps both; the archive marks it
 * gone from pickers. Everything already pointing at it still resolves, which is the difference
 * between withdrawing a market and losing its history.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type CityRow = components['schemas']['AdminCityRow'];
export type ServiceAreaRow = components['schemas']['AdminServiceAreaRow'];
export type LocalityRow = components['schemas']['AdminLocalityRow'];
export type TravelZoneRow = components['schemas']['AdminTravelZoneRow'];
type CityList = components['schemas']['AdminCityListResponse'];
export type StateRow = components['schemas']['StateRow'];
export type DistrictRow = components['schemas']['DistrictRow'];
type StateList = components['schemas']['StateListResponse'];
type CityCreateSchema = components['schemas']['CityCreateIn'];
type CityCreateRequired = 'district_id' | 'name' | 'latitude' | 'longitude';
/** The server defaults radius, metro, sort and the rest; the request may omit them. */
export type CityCreateIn = Pick<CityCreateSchema, CityCreateRequired> &
  Partial<Omit<CityCreateSchema, CityCreateRequired>>;
export type CityPatchIn = components['schemas']['CityPatchIn'];
export type LocalityIn = components['schemas']['LocalityIn'];
export type LocalityPatchIn = components['schemas']['LocalityPatchIn'];
export type TravelZoneIn = components['schemas']['TravelZoneIn'];
export type TravelZonePatchIn = components['schemas']['TravelZonePatchIn'];
export type TravelZoneCreated = components['schemas']['TravelZoneCreateResponse'];
export type WaitlistSummary = components['schemas']['AdminCityWaitlistSummary'];

export interface CityQuery extends Record<string, unknown> {
  page: number;
  page_size: number;
  launch_stage?: string;
  include_archived?: boolean;
}

/** Exported for its own test. `include_archived: false` is a filter; absent is the absence of one. */
export function cityQueryString(params: CityQuery): string {
  const search = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.page_size),
  });
  if (params.launch_stage) search.set('launch_stage', params.launch_stage);
  if (params.include_archived !== undefined) {
    search.set('include_archived', String(params.include_archived));
  }
  return search.toString();
}

export function useCities(params: CityQuery) {
  return useQuery({
    queryKey: keys.geo.cities(params),
    queryFn: () => authedClient.request<CityList>(`/v1/admin/geo/cities?${cityQueryString(params)}`),
  });
}

export function useServiceAreas(cityId: number | undefined) {
  return useQuery({
    queryKey: keys.geo.areas(cityId ?? 0),
    queryFn: () => authedClient.request<ServiceAreaRow[]>(`/v1/admin/geo/cities/${cityId}/service-areas`),
    enabled: cityId !== undefined,
  });
}

/** Only ever read inside an open city's panel, so the id is always known. */
export function useLocalities(cityId: number) {
  return useQuery({
    queryKey: keys.geo.localities(cityId),
    queryFn: () => authedClient.request<LocalityRow[]>(`/v1/admin/geo/cities/${cityId}/localities`),
  });
}

export function useTravelZones(cityId: number | undefined) {
  return useQuery({
    queryKey: keys.geo.zones(cityId ?? 0),
    queryFn: () => authedClient.request<TravelZoneRow[]>(`/v1/admin/geo/cities/${cityId}/travel-zones`),
    enabled: cityId !== undefined,
  });
}

/** The launch waitlist of one open city: counts, the source split, a weekly trend and masked
 * recent joins (`GET /v1/admin/geo/cities/{id}/waitlist`, admin doc 12 G8). */
export function useCityWaitlist(cityId: number) {
  return useQuery({
    queryKey: keys.geo.waitlist(cityId),
    queryFn: () => authedClient.request<WaitlistSummary>(`/v1/admin/geo/cities/${cityId}/waitlist`),
  });
}

/** States are seeded reference data — the public picker read, the same 36 the website uses. */
export function useStates() {
  return useQuery({
    queryKey: keys.geo.states(),
    queryFn: () => authedClient.request<StateList>('/v1/states'),
  });
}

export function useDistricts(stateId: number | undefined) {
  return useQuery({
    queryKey: keys.geo.districts(stateId ?? 0),
    queryFn: () => authedClient.request<DistrictRow[]>(`/v1/admin/geo/districts?state_id=${stateId}`),
    enabled: stateId !== undefined,
  });
}

/** Invalidates the whole namespace: a stage change moves the list, and areas hang off the city. */
function useGeoMutation<TArgs, TData = unknown>(fn: (args: TArgs) => Promise<TData>) {
  const client = useQueryClient();
  return useMutation<TData, unknown, TArgs>({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.geo.all() }),
  });
}

export function useSetLaunchStage() {
  return useGeoMutation(({ cityId, stage, reason }: { cityId: number; stage: string; reason: string }) =>
    authedClient.request<components['schemas']['LaunchStageResponse']>(
      `/v1/admin/geo/cities/${cityId}/launch-stage`,
      { method: 'POST', body: { stage, reason } },
    ),
  );
}

export function useArchiveCity() {
  return useGeoMutation(({ cityId, reason }: { cityId: number; reason: string }) =>
    authedClient.request<void>(`/v1/admin/geo/cities/${cityId}`, { method: 'DELETE', body: { reason } }),
  );
}

export function useCreateServiceArea() {
  return useGeoMutation(({ cityId, body }: { cityId: number; body: components['schemas']['ServiceAreaIn'] }) =>
    authedClient.request<ServiceAreaRow>(`/v1/admin/geo/cities/${cityId}/service-areas`, {
      method: 'POST',
      body,
    }),
  );
}

/**
 * Delete an area typed in error. The server refuses 409 while a pandit serves it, or when it is
 * the last area of a launched city — pausing is the way to stop dispatch there.
 */
export function useDeleteServiceArea() {
  return useGeoMutation((areaId: number) =>
    authedClient.request<void>(`/v1/admin/geo/service-areas/${areaId}`, { method: 'DELETE' }),
  );
}

export function usePatchServiceArea() {
  return useGeoMutation(
    ({ areaId, patch }: { areaId: number; patch: components['schemas']['ServiceAreaPatchIn'] }) =>
      authedClient.request<ServiceAreaRow>(`/v1/admin/geo/service-areas/${areaId}`, {
        method: 'PATCH',
        body: patch,
      }),
  );
}

/* ── City identity, districts, localities, travel rings ───────────────────────────────────
   A city's slug and lifecycle are not here: the slug is minted once and the stage has its own
   audited action. A ring's bounds are not patchable at all — once a quote pinned them they are
   history, and the legal path is retire + create. */

export function useCreateDistrict() {
  return useGeoMutation(({ stateId, name }: { stateId: number; name: string }) =>
    authedClient.request<DistrictRow>('/v1/admin/geo/districts', {
      method: 'POST',
      body: { state_id: stateId, name },
    }),
  );
}

export function useCreateCity() {
  return useGeoMutation((body: CityCreateIn) =>
    authedClient.request<CityRow>('/v1/admin/geo/cities', { method: 'POST', body }),
  );
}

export function usePatchCity() {
  return useGeoMutation(({ cityId, patch }: { cityId: number; patch: CityPatchIn }) =>
    authedClient.request<CityRow>(`/v1/admin/geo/cities/${cityId}`, { method: 'PATCH', body: patch }),
  );
}

export function useCreateLocality() {
  return useGeoMutation(({ cityId, body }: { cityId: number; body: LocalityIn }) =>
    authedClient.request<LocalityRow>(`/v1/admin/geo/cities/${cityId}/localities`, {
      method: 'POST',
      body,
    }),
  );
}

/** Only while nothing points at it; the server names what still does with a 409 otherwise. */
export function useDeleteLocality() {
  return useGeoMutation((localityId: number) =>
    authedClient.request<void>(`/v1/admin/geo/localities/${localityId}`, { method: 'DELETE' }),
  );
}

export function usePatchLocality() {
  return useGeoMutation(({ localityId, patch }: { localityId: number; patch: LocalityPatchIn }) =>
    authedClient.request<LocalityRow>(`/v1/admin/geo/localities/${localityId}`, {
      method: 'PATCH',
      body: patch,
    }),
  );
}

export function useCreateTravelZone() {
  return useGeoMutation(({ cityId, body }: { cityId: number; body: TravelZoneIn }) =>
    authedClient.request<TravelZoneCreated>(`/v1/admin/geo/cities/${cityId}/travel-zones`, {
      method: 'POST',
      body,
    }),
  );
}

export function usePatchTravelZone() {
  return useGeoMutation(({ zoneId, patch }: { zoneId: number; patch: TravelZonePatchIn }) =>
    authedClient.request<TravelZoneRow>(`/v1/admin/geo/travel-zones/${zoneId}`, {
      method: 'PATCH',
      body: patch,
    }),
  );
}
